import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import { getActiveTab, getCurrentUserId } from "features/authSlice";
import {
    selectIsNativeAppBannerDismissed,
    setNativeAppBannerDismissed,
} from "features/nativeAppPromoSlice";
import {
    useGetAdEligibilityQuery,
    useGetLoAdEligibilityQuery,
    useUpdateAdEligibilityMutation,
} from "pages/authentication/userService";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { NATIVE_APP_AD_ELIGIBILITY_KEY, NATIVE_APP_BANNER_DISMISS_KEY } from "utilities/Constant";

function readCachedAdEligibility(): boolean | undefined {
    try {
        const raw = sessionStorage.getItem(NATIVE_APP_AD_ELIGIBILITY_KEY);
        if (raw === "true") return true;
        if (raw === "false") return false;
        return undefined;
    } catch {
        return undefined;
    }
}

export const useNativeAppPromoVisibility = () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const userId = useSelector(getCurrentUserId);
    const activeTab = useSelector(getActiveTab);
    const { data: featureFlag } = useGetFeatureFlagsQuery();
    const bannerDismissed = useSelector(selectIsNativeAppBannerDismissed);

    const isNativeAppPromoDashboardHome = useMemo(() => {
        const onDashboardRoot = pathname === "/dashboard" || pathname === "/dashboard/";
        const isHomeTab = activeTab === "" || activeTab === NavigationIds.Home;
        return onDashboardRoot && isHomeTab;
    }, [pathname, activeTab]);

    const cachedAdEligibility = readCachedAdEligibility();
    const skipAdEligibilityGet = Boolean(userId) && cachedAdEligibility === true;
    const { data: adEligibilityFromQuery, isSuccess } = useGetAdEligibilityQuery(userId, {
        skip: !userId || skipAdEligibilityGet || !isNativeAppPromoDashboardHome,
    });
    const { data: loAdEligibilityFromQuery } = useGetLoAdEligibilityQuery(userId, {
        skip: !userId || !isNativeAppPromoDashboardHome,
    });
    const [updateAdEligibility] = useUpdateAdEligibilityMutation();

    useEffect(() => {
        if (!isNativeAppPromoDashboardHome) return;
        if (!userId || skipAdEligibilityGet) return;
        if (!isSuccess) return;
        if (adEligibilityFromQuery === undefined) return;

        if (cachedAdEligibility !== true && adEligibilityFromQuery === true) {
            void updateAdEligibility({ userId, eligible: false });
        }
        sessionStorage.setItem(
            NATIVE_APP_AD_ELIGIBILITY_KEY,
            adEligibilityFromQuery ? "true" : "false"
        );
    }, [
        isNativeAppPromoDashboardHome,
        userId,
        skipAdEligibilityGet,
        isSuccess,
        adEligibilityFromQuery,
        cachedAdEligibility,
        updateAdEligibility,
    ]);

    const adEligibility =
        adEligibilityFromQuery !== undefined ? adEligibilityFromQuery : cachedAdEligibility;

    const isFlagOn = Boolean(
        (featureFlag as unknown as Record<string, unknown> | undefined)?.nativeAppPromoEnabled
    );
    const isAdEligible = adEligibility === true;

    const isPromoCardVisible = useMemo(
        () => isNativeAppPromoDashboardHome && isFlagOn && isAdEligible,
        [isNativeAppPromoDashboardHome, isFlagOn, isAdEligible]
    );

    const isBannerVisible = useMemo(
        () => isNativeAppPromoDashboardHome && isFlagOn && isAdEligible && !bannerDismissed,
        [isNativeAppPromoDashboardHome, isFlagOn, isAdEligible, bannerDismissed]
    );

    const dismissBanner = useCallback(() => {
        sessionStorage.setItem(NATIVE_APP_AD_ELIGIBILITY_KEY, "false");
        sessionStorage.setItem(NATIVE_APP_BANNER_DISMISS_KEY, "true");
        dispatch(setNativeAppBannerDismissed(true));
    }, [dispatch]);

    return { isPromoCardVisible, isBannerVisible, dismissBanner, loAdEligibilityFromQuery };
};
